// src/pages/AgentManagement/modals/EditAgentModal.tsx
// Modal for editing existing agent — mục 18, REQUIRE_FIXBUGS.md: hiện thêm Tên đăng nhập (chỉ
// xem, bất biến sau khi tạo), Email/Vai trò (sửa được, đồng bộ sang tài khoản đăng nhập gắn với
// đại lý) — cùng bộ field như CreateAgentModal, trừ mật khẩu.

import React from "react";
import {
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Row,
  Col,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useLocation } from "react-router-dom";

import { IAgent } from "slices/agentManagement/types";
import {
  VN_MOBILE_PHONE_REGEX,
  VN_MOBILE_PHONE_ERROR_MESSAGE,
} from "helpers/phoneValidation";
import { PageActionPermission } from "services/pageActionPermission/PageActionPermission";
import { Department, Role } from "pages/UserManagement/types";
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('agent_management');

interface EditAgentModalProps {
  isOpen: boolean;
  toggle: () => void;
  agent: IAgent | null;
  roles: Role[];
  departments: Department[];
  onSubmit: (values: any) => void;
}

// Validation schema for edit (code is disabled, so no validation needed). Email/roleId chỉ bắt
// buộc khi đại lý đã có tài khoản đăng nhập (hasAccount) — đại lý cũ chưa có tài khoản thì 2 field
// này không hiển thị trên form nên cũng không cần validate.
const buildEditAgentSchema = (hasAccount: boolean) =>
  Yup.object().shape({
    code: Yup.string().nullable(), // No validation for disabled field
    name: Yup.string()
      .required(st('ten_dai_ly_khong_duoc_bo_error'))
      .min(3, st('ten_phai_co_it_nhat_3_error'))
      .max(150, st('ten_khong_duoc_qua_150_ky_error')),
    address: Yup.string()
      .required(st('dia_chi_khong_duoc_bo_trong_error'))
      .max(255, st('dia_chi_khong_duoc_qua_255_error')),
    commissionRate: Yup.number()
      .typeError(st('ty_le_hoa_hong_phai_la_error'))
      .required(st('ty_le_hoa_hong_khong_duoc_error'))
      .min(0, st('ty_le_hoa_hong_khong_duoc_error_2'))
      .max(100, st('ty_le_hoa_hong_khong_duoc_error_3')),
    prizePayoutCommissionRate: Yup.number()
      .typeError(st('ty_le_hoa_hong_tra_thuong_error'))
      .required(st('ty_le_hoa_hong_tra_thuong_error_2'))
      .min(0, st('ty_le_hoa_hong_tra_thuong_error_3'))
      .max(100, st('ty_le_hoa_hong_tra_thuong_error_4')),
    phoneNumber: Yup.string()
      .required(st('so_dien_thoai_khong_duoc_bo_error'))
      .matches(VN_MOBILE_PHONE_REGEX, VN_MOBILE_PHONE_ERROR_MESSAGE),
    status: Yup.string().required(st('vui_long_chon_trang_thai_error')),
    email: hasAccount
      ? Yup.string()
          .email(st('dinh_dang_email_khong_hop_le_error'))
          .required(st('email_khong_duoc_bo_trong_error'))
      : Yup.string().email(st('dinh_dang_email_khong_hop_le_error')),
    roleId: hasAccount
      ? Yup.string().required(st('vai_tro_khong_duoc_bo_trong_error'))
      : Yup.string(),
  });

const AGENT_STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: "Active", label: st('dang_hoat_dong_label') },
  { value: "Inactive", label: st('ngung_hoat_dong_label') },
  { value: "Locked", label: st('tam_khoa_label') },
];

const EditAgentModal: React.FC<EditAgentModalProps> = ({
  isOpen,
  toggle,
  agent,
  roles,
  departments,
  onSubmit,
}) => {
  const location = useLocation();
  if (!agent) return null;

  const hasAccount = !!agent.username;

  // Convert decimal to percentage for display (0.15 → 15)
  const initialValues = {
    code: agent.code || "",
    name: agent.name || "",
    address: agent.address || "",
    commissionRate: agent.commissionRate
      ? (agent.commissionRate * 100).toString()
      : "",
    prizePayoutCommissionRate: agent.prizePayoutCommissionRate
      ? (agent.prizePayoutCommissionRate * 100).toString()
      : "",
    phoneNumber: agent.phoneNumber || "",
    status: agent.status || "Active",
    email: agent.email || "",
    roleId: agent.roleId ? String(agent.roleId) : "",
    departmentId: agent.departmentId ? String(agent.departmentId) : "",
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <ModalHeader toggle={toggle}>
        <i className="ri-edit-line me-2"></i>
        {st('sua_dai_ly_title')}
      </ModalHeader>

      <Formik
        initialValues={initialValues}
        validationSchema={buildEditAgentSchema(hasAccount)}
        onSubmit={onSubmit}
        enableReinitialize
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          isSubmitting,
        }) => (
          <Form>
            <ModalBody>
              <Row>
                <Col lg={6}>
                  <FormGroup>
                    <Label for="code">
                      {st('ma_dai_ly_column')} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="code"
                      id="code"
                      data-action-code="edit-input-code"
                      data-action-name="Mã đại lý (Sửa)"
                      value={values.code}
                      disabled
                      className="bg-light"
                    />
                    <small className="text-muted">
                      {st('ma_dai_ly_khong_the_thay_hint')}
                    </small>
                  </FormGroup>
                </Col>

                <Col lg={6}>
                  <FormGroup>
                    <Label for="name">
                      {st('ten_dai_ly_column')} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="name"
                      id="name"
                      placeholder={st('vd_dai_ly_kim_thanh_placeholder')}
                      maxLength={150}
                      data-action-code="edit-input-name"
                      data-action-name="Tên đại lý (Sửa)"
                      value={values.name}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={
                        !PageActionPermission.canEnable(
                          location.pathname,
                          "edit-input-name",
                        )
                      }
                      invalid={touched.name && !!errors.name}
                    />
                    {touched.name && errors.name && (
                      <FormFeedback>{errors.name}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>

                <Col lg={12}>
                  <FormGroup>
                    <Label for="address">
                      {st('dia_chi_column')} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="address"
                      id="address"
                      placeholder={st('nhap_dia_chi_hoat_dong_placeholder')}
                      maxLength={255}
                      data-action-code="edit-input-address"
                      data-action-name="Địa chỉ (Sửa)"
                      value={values.address}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={
                        !PageActionPermission.canEnable(
                          location.pathname,
                          "edit-input-address",
                        )
                      }
                      invalid={touched.address && !!errors.address}
                    />
                    {touched.address && errors.address && (
                      <FormFeedback>{errors.address}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>

                <Col lg={6}>
                  <FormGroup>
                    <Label for="commissionRate">
                      {st('ty_le_hoa_hong_label')} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="number"
                      name="commissionRate"
                      id="commissionRate"
                      placeholder={st('vd_15_placeholder')}
                      min={0}
                      max={100}
                      step={0.01}
                      data-action-code="edit-input-commission-rate"
                      data-action-name="Tỷ lệ hoa hồng (Sửa)"
                      value={values.commissionRate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={
                        !PageActionPermission.canEnable(
                          location.pathname,
                          "edit-input-commission-rate",
                        )
                      }
                      invalid={
                        touched.commissionRate && !!errors.commissionRate
                      }
                    />
                    {touched.commissionRate && errors.commissionRate && (
                      <FormFeedback>{errors.commissionRate}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>

                <Col lg={6}>
                  <FormGroup>
                    <Label for="prizePayoutCommissionRate">
                      {st('ty_le_hoa_hong_tra_thuong_label')} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="number"
                      name="prizePayoutCommissionRate"
                      id="prizePayoutCommissionRate"
                      placeholder={st('vd_5_placeholder')}
                      min={0}
                      max={100}
                      step={0.01}
                      data-action-code="edit-input-commission-rate"
                      data-action-name="Tỷ lệ hoa hồng trả thưởng (Sửa)"
                      value={values.prizePayoutCommissionRate}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={
                        !PageActionPermission.canEnable(
                          location.pathname,
                          "edit-input-commission-rate",
                        )
                      }
                      invalid={
                        touched.prizePayoutCommissionRate && !!errors.prizePayoutCommissionRate
                      }
                    />
                    {touched.prizePayoutCommissionRate && errors.prizePayoutCommissionRate && (
                      <FormFeedback>{errors.prizePayoutCommissionRate}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>

                <Col lg={6}>
                  <FormGroup>
                    <Label for="phoneNumber">
                      {st('so_dien_thoai_column')} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="tel"
                      name="phoneNumber"
                      id="phoneNumber"
                      placeholder={st('vd_0901234567_placeholder')}
                      maxLength={10}
                      data-action-code="edit-input-phone-number"
                      data-action-name="Số điện thoại (Sửa)"
                      value={values.phoneNumber}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={
                        !PageActionPermission.canEnable(
                          location.pathname,
                          "edit-input-phone-number",
                        )
                      }
                      invalid={touched.phoneNumber && !!errors.phoneNumber}
                    />
                    {touched.phoneNumber && errors.phoneNumber && (
                      <FormFeedback>{errors.phoneNumber}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>

                <Col lg={6}>
                  <FormGroup>
                    <Label for="status">
                      {st('trang_thai_column')} <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="select"
                      name="status"
                      id="status"
                      data-action-code="edit-input-status"
                      data-action-name="Trạng thái đại lý (Sửa)"
                      value={values.status}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={
                        !PageActionPermission.canEnable(
                          location.pathname,
                          "edit-input-status",
                        )
                      }
                      invalid={touched.status && !!errors.status}
                    >
                      {AGENT_STATUS_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Input>
                    {touched.status && errors.status && (
                      <FormFeedback>{errors.status}</FormFeedback>
                    )}
                  </FormGroup>
                </Col>
              </Row>

              <hr className="my-3" />
              <h6 className="mb-3">{st('tai_khoan_dang_nhap_cua_dai_title')}</h6>

              {!hasAccount ? (
                <Alert color="warning" className="py-2 mb-0">
                  <i className="ri-alert-line me-1"></i>
                  {st('dai_ly_nay_chua_co_tai_message')}
                </Alert>
              ) : (
                <Row>
                  <Col lg={6}>
                    <FormGroup>
                      <Label for="usernameView">{st('ten_dang_nhap_column')}</Label>
                      <Input
                        type="text"
                        id="usernameView"
                        value={agent.username || ""}
                        disabled
                        className="bg-light"
                      />
                      <small className="text-muted">
                        {st('khong_the_thay_doi_sau_khi_hint')}
                      </small>
                    </FormGroup>
                  </Col>

                  <Col lg={6}>
                    <FormGroup>
                      <Label for="email">
                        {st('email_column')} <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="email"
                        name="email"
                        id="email"
                        data-action-code="edit-input-email"
                        data-action-name="Email đại lý (Sửa)"
                        value={values.email}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={
                          !PageActionPermission.canEnable(
                            location.pathname,
                            "edit-input-email",
                          )
                        }
                        invalid={touched.email && !!errors.email}
                      />
                      {touched.email && errors.email && (
                        <FormFeedback>{errors.email}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>

                  <Col lg={6}>
                    <FormGroup>
                      <Label for="roleId">
                        {st('vai_tro_label')} <span className="text-danger">*</span>
                      </Label>
                      <Input
                        type="select"
                        name="roleId"
                        id="roleId"
                        data-action-code="edit-input-role"
                        data-action-name="Vai trò đại lý (Sửa)"
                        value={values.roleId}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={
                          !PageActionPermission.canEnable(
                            location.pathname,
                            "edit-input-role",
                          )
                        }
                        invalid={touched.roleId && !!errors.roleId}
                      >
                        <option value="">{st('chon_vai_tro_option')}</option>
                        {roles.map((role) => (
                          <option key={role.id} value={role.id}>
                            {role.name}
                          </option>
                        ))}
                      </Input>
                      {touched.roleId && errors.roleId && (
                        <FormFeedback>{errors.roleId}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>

                  <Col lg={6}>
                    <FormGroup>
                      <Label for="departmentId">{st('phong_ban_label')}</Label>
                      <Input
                        type="select"
                        name="departmentId"
                        id="departmentId"
                        data-action-code="edit-input-department"
                        data-action-name="Phòng ban đại lý (Sửa)"
                        value={values.departmentId}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        disabled={
                          !PageActionPermission.canEnable(
                            location.pathname,
                            "edit-input-department",
                          )
                        }
                      >
                        <option value="">{st('chon_phong_ban_option')}</option>
                        {departments.map((dept) => (
                          <option key={dept.id} value={dept.id}>
                            {dept.name}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>
              )}
            </ModalBody>

            <ModalFooter>
              {PageActionPermission.canDisplay(
                location.pathname,
                "button-edit-submit",
              ) && (
                <Button
                  type="submit"
                  color="primary"
                  data-action-code="button-edit-submit"
                  data-action-name="Lưu thay đổi đại lý"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      {st('dang_xu_ly_text')}
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line me-1"></i>
                      {st('luu_thay_doi_text')}
                    </>
                  )}
                </Button>
              )}
              <Button
                type="button"
                color="secondary"
                onClick={toggle}
                disabled={isSubmitting}
              >
                <i className="ri-close-line me-1"></i>
                {st('huy_button')}
              </Button>
            </ModalFooter>
          </Form>
        )}
      </Formik>
    </Modal>
  );
};

export default EditAgentModal;
